// Direct-path imports, not the barrel: importing './index' from a file the
// barrel re-exports creates a cycle, and admin is gated at zero.
import type {
  AfriexAsset,
  BusinessPaymentMethodInfo,
  SettlementPaymentMethod,
} from '@afriex/core/domain/types';

/**
 * Afriex's own bank accounts, the ones a fee invoice is paid into. Saved once
 * by admin personnel and picked per invoice by the payment currency.
 *
 * The shape is `SettlementPaymentMethod` from `@afriex/core`, which the server
 * persists: the account details live in `info`, whose keys depend on `channel`
 * (see `SettlementAccounts/settlementChannelForms` for which channel asks for
 * what), the
 * payment narration an invoice reproduces sits alongside them as
 * `info.instructions`, and archival is `meta.flags.isDeactivated`.
 */
export type SettlementPaymentMethodListParams = {
  page?: number;
  limit?: number;
  currencyCode?: AfriexAsset;
  /**
   * UI-only. The list endpoint has no deactivated filter, so this is stripped
   * before the request and applied to the returned page instead.
   */
  includeArchived?: boolean;
};

export type SettlementPaymentMethodPayload = Omit<
  SettlementPaymentMethod,
  'id' | 'createdAt' | 'updatedAt'
>;

/**
 * Archived accounts stay listed on the management tab but drop out of the
 * invoice form, so an invoice already raised against one still reads correctly.
 * Archival is the deactivation flag, and the filter is client-side: the server
 * list endpoint has no flag for it.
 */
export const isSettlementPaymentMethodArchived = (
  method: SettlementPaymentMethod,
): boolean => method.meta?.flags?.isDeactivated === true;

/**
 * Core types `info` as a union of every payment-method shape, and this screen
 * only ever manages bank accounts, so reads go through one narrowing accessor.
 */
export const readSettlementAccountInfo = (
  info: BusinessPaymentMethodInfo | undefined,
): Record<string, unknown> => (info ?? {}) as Record<string, unknown>;

/** Bank details are strings; anything else renders as an empty input. */
export const readSettlementAccountField = (
  info: BusinessPaymentMethodInfo | undefined,
  field: string,
): string => {
  const value = readSettlementAccountInfo(info)[field];
  return typeof value === 'string' ? value : '';
};
